// Classification step helper functions

import type { CategoryItem } from "src/shared/forms/components/CategoryBrowser/CategoryBrowser";
import type { MarketplaceType } from "src/app/models/MarketplaceCategory";
import type { AccountCategory } from "../../category/types/AccountCategory";
import type { Brand } from "./classificationConstants";

// Utility function for class names
export const classNames = (...classes: (string | undefined | false)[]): string => {
  return classes.filter(Boolean).join(" ");
};

// Convert AccountCategory to CategoryItem for CategoryBrowser
export const toCategoryItem = (cat: AccountCategory): CategoryItem => ({
  id: cat.id,
  name: cat.name,
  nameLocalized: cat.nameLocalized, // Pass through localized names for language switching
  hasChildren: cat.hasChildren,
  isActive: cat.isActive,
  parentCategoryId: cat.parentCategoryId,
});

// Get marketplace for category loading - always try "merchant" first
export const getMarketplaceForCategories = (): MarketplaceType => {
  return "merchant" as MarketplaceType;
};

// Get fallback marketplace from accounts
export const getFallbackMarketplace = (accounts: any[]): MarketplaceType | null => {
  if (accounts.length === 0) return null;

  // Extract unique marketplaces from accessible accounts
  const marketplaces = [...new Set(accounts.filter(acc => acc.marketplace !== "tokopedia").map(acc => acc.marketplace))];
  // Sort alphabetically and get first one
  const sortedMarketplaces = marketplaces.sort();
  return (sortedMarketplaces[0] as MarketplaceType) || null;
};

// Get display name for the selected category path
export const getSelectedCategoryDisplayName = (categoryPath?: CategoryItem[]): string => {
  if (categoryPath && categoryPath.length > 0) {
    return categoryPath.map((cat) => cat.name).join(" > ");
  }
  return "";
};

// Get placeholder text for category button
export const getCategoryPlaceholder = (rootLoading: boolean, rootError: string | null): string => {
  if (rootError) {
    return "Error loading categories";
  }
  if (rootLoading) {
    return "Loading categories...";
  }
  return "Browse categories...";
};

// Get error for specific field - improved matching
export const getFieldError = (errors: string[], fieldName: string): string | undefined => {
  const fieldMap: { [key: string]: string[] } = {
    category: ["Product category", "category"],
    brand: ["brand"],
    brandId: ["brand"],
    brandName: ["brand"],
  };

  const patterns = fieldMap[fieldName] || [fieldName];
  return errors.find((error) =>
    patterns.some((pattern) =>
      error.toLowerCase().includes(pattern.toLowerCase())
    )
  );
};

// Filter brands by search query
export const filterBrands = (brands: Brand[], searchQuery: string): Brand[] => {
  if (!searchQuery) return brands;
  return brands.filter((brand) =>
    brand.name.toLowerCase().includes(searchQuery.toLowerCase())
  );
};
